import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FeatherModule } from 'angular-feather';
import { UserService } from '../services/api-services/user.service';
import { NgIf } from '@angular/common';
import { environment } from '../environments/environment';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from '../dialog/dialog.component';
import { AuthService } from '../services/auth.service';
import { UserInfoService } from '../services/user.service';


@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    MatFormFieldModule,
    FeatherModule,
    MatCardModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatButtonModule,
    NgIf,
    MatProgressBarModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent implements OnInit {

  checked = true;
  hide = true;
  hideConfirm = true;

  initialEmail = '';
  initialUsername = '';

  profileForm: FormGroup = new FormGroup({});
  showUpdateSuccess = true;
  successMessage: string | null = null;
  errorMessage: string | null = null;

  isPageLoading = false;
  isLoading = false;

  constructor(private fb: FormBuilder, private userService: UserInfoService, private router: Router, private dialog: MatDialog,
    private authService: AuthService, private apiService: UserService, private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.profileForm = this.fb.group({
      username: [{value: '', disabled: true}, Validators.required],
      email: [{value: '', disabled: true}, [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required]
    }, { validators: profilePasswordMatchValidator });
    this.successMessage = null;
    this.errorMessage = null;

    this.getCurrentUser();
  }

  getCurrentUser() {
    this.userService.userInfo$.subscribe({
      next: response => {
        if (!environment.production) {
          console.log('response in profile: ', response);
        }
        if (response) {
          const username = response["username"];
          const email = response["email"];
          this.profileForm.patchValue({
            username: username,
            email: email
          });
          this.initialEmail = email;
          this.initialUsername = username;
          this.isPageLoading = false;
          this.changeDetector.detectChanges();
        } else {
          this.isPageLoading = true;
          this.changeDetector.detectChanges();
        }

      },
      error: error => {
        if (!environment.production) {
          console.log(error);
        }
        this.errorMessage = 'An error occurred while trying to get current user details. Please try again.';
        this.isPageLoading = false;
        this.changeDetector.detectChanges();
      },
      complete: () => {
        this.isPageLoading = false;
        this.changeDetector.detectChanges();
        console.log('is page loading', this.isPageLoading);
      }
    })
  }

  onSubmit() {
    this.isLoading = true;
    this.updatePassword();
  }

  updatePassword() {
    const newPassword = this.profileForm.get('password')?.value;

    if (!newPassword) {
      console.log('Password is required.');
      return;
    }

    this.apiService.updatePassword(newPassword).subscribe({
      next: response => {
        if (!environment.production) {
          console.log(response);
        }
      },
      error: error => {
        if (!environment.production) {
          console.log(error);
        }
        this.errorMessage = 'An error occurred while trying to update password. Please try again.';
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
        this.errorMessage = null;
        this.profileForm.get('password')?.reset();
        this.profileForm.get('confirmPassword')?.reset();
        // Refresh cached user info so the home banner won't reappear
        this.userService.loadUserInfo().subscribe();
        this.dialog.open(DialogComponent, {
          width: '420px',
          data: {
            dialogTitle: 'Password updated',
            content: 'Your password has been changed successfully. You are still logged in.',
            newButton: false,
            createFile: false
          }
        });
      }
    });
  }

  deleteAccount() {
    this.isPageLoading = true;
    this.apiService.deleteCurrentAccount().subscribe({
      next: response => {
        if (!environment.production) {
          console.log(response);
        }
        this.router.navigate(['/sign-in']);
      },
      error: error => {
        if (!environment.production) {
          console.log(error);
        }
        window.alert('Error while deleting current account. Please contact admin.');
        this.router.navigate(['/sign-in']);
      },
      complete: () => {
        this.router.navigate(['/sign-in']);
      }
    })
  }

  deleteDialog() {
    const dialogRef = this.dialog.open(DialogComponent,
      {
        width: '500px',
        data: {
          dialogTitle: 'Delete confirmation',
          content: 'Please confirm if you would like to proceed with the permanent deletion of your account.',
          newButton: true,
          newButtonName: 'Delete',
          createFile: false
        }
      }
    );

    dialogRef.componentInstance.buttonClicked.subscribe(() => {
      dialogRef.close();
      this.logoutAndDeleteAccount();
    });
  }

  logoutAndDeleteAccount(): void {
    this.isPageLoading = true;
    this.authService.setAuthenticate(false);
    this.apiService.deleteCurrentAccount().subscribe({
      next: response => {
        if (!environment.production) {
          console.log(response);
        }
        this.router.navigate(['/sign-in']);
      },
      error: error => {
        if (!environment.production) {
          console.log(error);
        }
        window.alert('Error while deleting current account. Please contact admin.');
        this.router.navigate(['/sign-in']);
      },
      complete: () => {
        this.router.navigate(['/sign-in']);
      }
    })
  }
}

export function profilePasswordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const password = control.get('password')?.value;
  const confirmPassword = control.get('confirmPassword')?.value;
  if (password && confirmPassword && password !== confirmPassword) {
    return { passwordMismatch: true };
  }
  return null;
}
